/**
 * Up Cart Drawer Integration for Horizon Theme
 * Bridges Up Cart events with theme's cart icon update system
 */

import { CartUpdateEvent } from '@theme/events';

class UpCartIntegration {
  constructor() {
    this.initListeners();
  }

  initListeners() {
    // Method 1: Listen to Up Cart Drawer's custom events
    // Up Cart typically uses these event names (try multiple variants)
    const upCartEvents = [
      'upcart:item-added',
      'upcart:item-removed',
      'upcart:cart-updated',
      'cart:updated', // Generic cart event
      'cart.requestComplete', // Shopify ajax cart
    ];

    upCartEvents.forEach(eventName => {
      document.addEventListener(eventName, this.handleUpCartUpdate);
    });

    // Method 2: Poll Shopify Cart API when Up Cart drawer opens/closes
    // Up Cart drawer element typically has these selectors
    const upCartObserverTargets = [
      '.upcart-drawer',
      '[data-upcart-drawer]',
      '#upcart-drawer',
    ];

    upCartObserverTargets.forEach(selector => {
      const element = document.querySelector(selector);
      if (element) {
        this.observeUpCartDrawer(element);
      }
    });

    // Method 3: Intercept fetch requests to cart endpoints
    this.interceptCartRequests();

    // Method 4: Fallback - poll cart count every 2 seconds when drawer is open
    this.startCartPolling();
  }

  /**
   * Handle Up Cart update events
   */
  handleUpCartUpdate = async (event) => {
    console.log('Up Cart event detected:', event.type, event.detail);
    await this.syncCartCount();
  };

  /**
   * Observe Up Cart drawer visibility changes
   */
  observeUpCartDrawer(element) {
    const observer = new MutationObserver((mutations) => {
      mutations.forEach((mutation) => {
        if (mutation.type === 'attributes' &&
            (mutation.attributeName === 'class' ||
             mutation.attributeName === 'style' ||
             mutation.attributeName === 'aria-hidden')) {
          // Drawer visibility changed, sync cart count
          this.syncCartCount();
        }
      });
    });

    observer.observe(element, {
      attributes: true,
      attributeFilter: ['class', 'style', 'aria-hidden', 'open'],
    });
  }

  /**
   * Intercept fetch requests to Shopify cart endpoints
   */
  interceptCartRequests() {
    // Intercept modern fetch() API
    const originalFetch = window.fetch;
    window.fetch = async (...args) => {
      const response = await originalFetch(...args);

      const url = args[0];
      if (typeof url === 'string' &&
          (url.includes('/cart/add') ||
           url.includes('/cart/update') ||
           url.includes('/cart/change') ||
           url.includes('/cart/clear'))) {
        console.log('Cart API request detected (fetch):', url);
        // Wait a bit for Up Cart to finish processing
        setTimeout(() => this.syncCartCount(), 300);
      }

      return response;
    };

    // Also intercept older XMLHttpRequest API (for enhanced Ajax compatibility)
    const originalOpen = XMLHttpRequest.prototype.open;
    const originalSend = XMLHttpRequest.prototype.send;
    const self = this;

    XMLHttpRequest.prototype.open = function(method, url, ...args) {
      this._url = url;
      return originalOpen.call(this, method, url, ...args);
    };

    XMLHttpRequest.prototype.send = function(...args) {
      if (this._url &&
          (this._url.includes('/cart/add') ||
           this._url.includes('/cart/update') ||
           this._url.includes('/cart/change') ||
           this._url.includes('/cart/clear'))) {

        this.addEventListener('load', function() {
          console.log('Cart API request detected (XHR):', this._url);
          setTimeout(() => self.syncCartCount(), 300);
        });
      }
      return originalSend.apply(this, args);
    };
  }

  /**
   * Poll cart count (fallback method)
   */
  startCartPolling() {
    // Only poll when Up Cart drawer is visible
    setInterval(() => {
      const drawer = document.querySelector('.upcart-drawer[style*="display: block"], .upcart-drawer:not([style*="display: none"])');
      if (drawer) {
        this.syncCartCount();
      }
    }, 2000);
  }

  /**
   * Fetch current cart count from Shopify and update theme's cart icon
   */
  async syncCartCount() {
    try {
      const response = await fetch('/cart.js', {
        method: 'GET',
        headers: {
          'Content-Type': 'application/json',
        },
      });

      if (!response.ok) {
        throw new Error('Failed to fetch cart');
      }

      const cart = await response.json();
      const itemCount = cart.item_count || 0;

      console.log('Syncing cart count:', itemCount);

      // Dispatch theme's CartUpdateEvent to trigger cart icon update
      document.dispatchEvent(
        new CartUpdateEvent({}, 'upcart-integration', {
          itemCount: itemCount,
          source: 'upcart-integration',
        })
      );
    } catch (error) {
      console.error('Error syncing cart count:', error);
    }
  }
}

// Initialize when DOM is ready
if (document.readyState === 'loading') {
  document.addEventListener('DOMContentLoaded', () => {
    new UpCartIntegration();
  });
} else {
  new UpCartIntegration();
}
